/* @flow */


import bidbase from './bidbase';
import { extend, isNonEmptyObject, centsToDollar } from '../utils/coreutils';
import { isFetchSupported, fetchJSON } from '../utils/fetch';
import { generateUUID, apiName, apiVersion } from '../utils';
import {
  inIframe,
  getViewportDimensions,
  getReferrer,
  BrowserUtil,
  getPageUrl,
  getClientHintsUA,
  isSecure,
  isJSONSupported,
  getHostname,
  getLanguage
} from '../utils/browserutil';
import { isDNT } from '../utils/privacyutils';
import { getRTBUserEids } from '../idproviders/idputils';
import { logError } from '../utils/logger';
import { isdebugPartnerEnabled } from '../utils/debug';
import adconfig from '../adconfig';

/**
 * Base class for OpenRTB 2.6 bidders.
 * Provides default request construction, headers, and response handling.
 */

class _openrtbBase extends bidbase {
  _sendDisplayManager: boolean;
  _reqContentType: string;
  _reqWithOpenRTBVersion: boolean;
  _reqWithCredentials: boolean;
  _openrtbVersion: number;
  _sendSChainRequestId: boolean;
  _bidPriceInCents: boolean;
  _mapPosToImpID: { [string]: string };
  constructor(name: string) {
    super(name);
    this._isSupported = isJSONSupported() && isFetchSupported();
    // Whether to include SDK/library identifiers in each imp (OpenRTB 2.6)
    // Bidders may override in their constructor if their endpoint disallows these fields
    this._sendDisplayManager = true;
    this._reqContentType = 'application/json';
    this._reqWithOpenRTBVersion = false;
    this._reqWithCredentials = false;
    // Default to latest supported OpenRTB version for this base
    this._openrtbVersion = 2.6;
    // Whether to include request ID as rid in supply chain first node
    this._sendSChainRequestId = false;
    // Whether bid prices are in cents (defaults to false for backward compatibility)
    this._bidPriceInCents = false;
    // Map position names to impression UUIDs (bounded by number of positions)
    this._mapPosToImpID = {};
  }

  // ----- Overridable hooks -----
  // eslint-disable-next-line no-unused-vars
  _getEndpoint(posList: Array<string>): string {
    return this._config.bidUrl;
  }

  _getImpExt(pos: string): anyObject {
    const gpid = adconfig.getGPID(pos);
    const adslot = adconfig.getPosSettings(pos).adUnitPath || '';
    return { gpid, data: { adserver: { name: 'gam', adslot } } };
  }

  // eslint-disable-next-line no-unused-vars
  _getImpFormatExt(pos: string, format: anyObject): anyObject {
    // Partner-specific ext for banner.format[].ext
    return {};
  }

  _getSiteExt(): anyObject {
    // Partner-specific ext for site.ext
    return {};
  }

  _getDeviceExt(): anyObject {
    // Partner-specific ext for device.ext
    return {};
  }

  _getUserExt(): anyObject {
    // Partner-specific ext for user.ext
    return {};
  }

  _getUserExtra(): anyObject {
    // Partner-specific fields for user object top-level (e.g., user.eids)
    return {};
  }

  _getSourceExt(): anyObject {
    // Partner-specific ext for source.ext
    return {};
  }

  _getRegsExt(): anyObject {
    // Partner-specific ext for regs.ext
    return {};
  }

  // eslint-disable-next-line no-unused-vars
  _getTagId(pos: string): string {
    // Partner-specific tagid for impression object
    // Return empty string by default; subclasses can override to provide tagid
    return '';
  }

  _buildImps(posList: Array<string>): Array<anyObject> {
    return posList.map(pos => {
      const posSettings = adconfig.getPosSettings(pos) || {};
      const { width = 0, height = 0, sizes = [] } = posSettings;
      // Normalize sizes to an array of [w,h] tuples
      // The Array.isArray check is not necessary here; normSizes is always an array due to flat().
      const normSizes = [sizes].flat();
      const format = normSizes.map(([w, h]) => {
        const fmt: anyObject = { w, h };
        const formatExt = this._getImpFormatExt(pos, fmt);
        if (isNonEmptyObject(formatExt)) {
          fmt.ext = formatExt;
        }
        return fmt;
      });
      let primaryW = width;
      let primaryH = height;
      if ((primaryW === 0 || primaryH === 0) && format.length) {
        primaryW = format[0].w || 0;
        primaryH = format[0].h || 0;
      }
      // Add placement position
      const placementPosition = adconfig.getPlacementPosition(pos);

      // Generate UUID for impression ID (OpenRTB standard)
      const impid = generateUUID();
      this._mapPosToImpID[pos] = impid;

      const imp: anyObject = {
        id: impid,
        secure: isSecure() ? 1 : 0,
        banner: {
          topframe: inIframe() ? 0 : 1,
          format,
          pos: placementPosition,
          w: primaryW,
          h: primaryH
        }
      };
      // Conditionally add tagid only if partner provides a non-empty value
      // Per OpenRTB spec, optional fields should be omitted when not applicable
      const tagid = this._getTagId(pos);
      if (tagid && tagid.length > 0) {
        imp.tagid = tagid;
      }
      if (this._sendDisplayManager) {
        imp.displaymanager = apiName();
        imp.displaymanagerver = apiVersion();
      }
      const extVal: anyObject = this._getImpExt(pos);
      if (isNonEmptyObject(extVal)) {
        imp.ext = extVal;
      }
      return imp;
    });
  }

  // Optional hook: subclasses may override to provide a site id from posList
  // eslint-disable-next-line no-unused-vars
  _getSiteId(_posList?: Array<string>): string | void {
    return undefined;
  }

  _buildSite(_posList?: Array<string>): anyObject {
    const site: anyObject = {
      domain: getHostname(),
      page: getPageUrl(),
      ref: getReferrer(),
      publisher: { domain: getHostname() },
      ext: { data: { documentLang: getLanguage() } },
      content: { language: getLanguage() }
    };
    // If the subclass overrides _getSiteId, use the returned value for site.id
    const sid = this._getSiteId(_posList);
    if (typeof sid === 'string' && sid.length > 0) {
      site.id = sid;
    }
    // Merge partner-specific site.ext
    const siteExt = this._getSiteExt();
    if (isNonEmptyObject(siteExt)) {
      site.ext = extend(true, {}, site.ext, siteExt);
    }
    return { site };
  }

  _getRequestExt(): anyObject {
    // Partner-specific ext to be merged at root level
    return {};
  }

  _augmentHeaders(h: { [string]: string }): { [string]: string } {
    const headers = extend(true, {}, h, {
      'Content-Type': this._reqContentType
    });
    if (this._reqWithOpenRTBVersion) {
      headers['x-openrtb-version'] = String(this._openrtbVersion);
    }
    return headers;
  }

  _processResponse(
    posList: Array<string>,
    response: RTBBidResponse,
    ret: hbPartnerData
  ): hbPartnerData {
    if (!this._isValidRTBResponse(response)) {
      return ret;
    }
    const bids = (response.seatbid || []).flatMap(v => v.bid || []);
    // Build a map of impid -> bid for O(1) lookup
    const bidMap = {};
    bids.forEach(bid => {
      if (bid && bid.impid) {
        bidMap[bid.impid] = bid;
      }
    });

    // Iterate through positions and look up bids by UUID
    posList.forEach(pos => {
      const impid = this._mapPosToImpID[pos];
      const bid = bidMap[impid];
      if (bid) {
        const { price = 0, adm = '', nurl = '' } = bid;
        // Convert price from cents to dollars if bidder sends prices in cents
        const priceStr = this._bidPriceInCents
          ? centsToDollar(Number(price))
          : String(price);
        if (priceStr.length > 0 && adm.length > 0) {
          this._handleBidPosData(ret, pos, priceStr, impid, adm, nurl || '');
        }
      }
    });
    return ret;
  }

  // For partners that need it; default no-op
  // eslint-disable-next-line no-empty-function, no-unused-vars
  _updateSyncDomainFromResponse(_response: RTBBidResponse) {}

  // ----- Core OpenRTB build/fetch -----
  _buildDevicePart(): { device: RTBDevice } {
    const sua = getClientHintsUA();
    const device: RTBDevice = {
      ua: BrowserUtil.getUserAgent(),
      language: navigator.language || '',
      dnt: isDNT() ? 1 : 0,
      w: window.screen.width,
      h: window.screen.height,
      ext: {
        vpw: getViewportDimensions()[0],
        vph: getViewportDimensions()[1]
      }
    };
    // Include Structured UA only for OpenRTB 2.6+
    if (sua && this._openrtbVersion >= 2.6) {
      device.sua = sua;
    }
    // Merge partner-specific device.ext
    const deviceExt = this._getDeviceExt();
    if (isNonEmptyObject(deviceExt)) {
      device.ext = extend(true, {}, device.ext, deviceExt);
    }
    return { device };
  }

  _buildUserIdsPart(): anyObject {
    const eidObj = getRTBUserEids() || {};
    const eids =
      eidObj && eidObj.user && Array.isArray(eidObj.user.eids)
        ? eidObj.user.eids
        : [];

    // Get partner-specific extensions
    const userExt = this._getUserExt();
    const userExtra = this._getUserExtra();

    // Return empty if no eids and no partner-specific fields
    if (
      !eids.length &&
      !isNonEmptyObject(userExt) &&
      !isNonEmptyObject(userExtra)
    ) {
      return {};
    }

    const user: anyObject = { ext: {} };

    // Add eids if present
    if (eids.length) {
      user.ext.eids = eids;
    }

    // Merge partner-specific user.ext
    if (isNonEmptyObject(userExt)) {
      user.ext = extend(true, {}, user.ext, userExt);
    }

    // Merge partner-specific user top-level fields (e.g., user.eids for Prebid compatibility)
    if (isNonEmptyObject(userExtra)) {
      extend(true, user, userExtra);
    }

    return { user };
  }

  _buildRequestBody(posList: Array<string>): anyObject {
    // Clear mapping at start of each request to avoid stale mappings
    this._mapPosToImpID = {};
    const requestId = generateUUID();
    const privacyString = this._getPrivacyString();
    const regsExt = this._getRegsExt();
    const regsExtMerged = extend(true, {}, privacyString, regsExt);
    const reqBody = extend(
      true,
      {},
      {
        id: requestId,
        at: 1,
        tmax: this._config.timeout,
        cur: ['USD'],
        // $FlowFixMe partner-scoped debug helper attached on default export
        test: isdebugPartnerEnabled(this._name) ? 1 : 0,
        source: { tid: generateUUID() },
        imp: this._buildImps(posList)
      },
      this._buildSite(posList),
      this._buildDevicePart(),
      this._buildUserIdsPart(),
      this._buildSupplyChainPart(requestId),
      { regs: { ext: regsExtMerged } }
    );

    // Merge partner-specific request ext
    const requestExt = this._getRequestExt();
    if (isNonEmptyObject(requestExt)) {
      reqBody.ext = extend(true, {}, reqBody.ext || {}, requestExt);
    }

    return reqBody;
  }

  _buildSupplyChainPart(requestId: string = ''): anyObject {
    // Only pass requestId if flag is enabled
    const schain = this._sendSChainRequestId
      ? this.getSupplyChain(requestId)
      : this.getSupplyChain();

    const sourceExt = this._getSourceExt();
    const hasSourceExt = isNonEmptyObject(sourceExt);
    const hasSchain = isNonEmptyObject(schain);

    if (!hasSchain && !hasSourceExt) {
      return {};
    }

    // Place schain per OpenRTB version: 2.6 => source.schain, 2.5 => source.ext.schain, <=2.4 => ext.schain
    if (this._openrtbVersion >= 2.6) {
      const result: anyObject = { source: {} };
      if (hasSchain) {
        result.source.schain = schain;
      }
      if (hasSourceExt) {
        result.source.ext = sourceExt;
      }
      return result;
    }
    if (this._openrtbVersion >= 2.5) {
      const result: anyObject = { source: { ext: {} } };
      if (hasSchain) {
        result.source.ext.schain = schain;
      }
      if (hasSourceExt) {
        result.source.ext = extend(true, {}, result.source.ext, sourceExt);
      }
      return result;
    }
    // OpenRTB <= 2.4
    const result: anyObject = { ext: {} };
    if (hasSchain) {
      result.ext.schain = schain;
    }
    if (hasSourceExt) {
      result.source = { ext: sourceExt };
    }
    return result;
  }

  _getBids(posList: Array<string>): Promise<RTBBidResponse> {
    const reqBody = this._buildRequestBody(posList);
    const opt = {
      data: {},
      timeout: this._config.timeout,
      logPrefix: posList.join(',')
    };
    const headers = this._augmentHeaders({});
    const req: anyObject = {
      method: 'POST',
      headers,
      body: JSON.stringify(reqBody)
    };
    if (this._reqWithCredentials) {
      req.credentials = 'include';
    }
    return fetchJSON(this._getEndpoint(posList), opt, req).then(response => {
      this._updateSyncDomainFromResponse(response);
      return response;
    });
  }

  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    return this._getBids(posList).then(
      response => this._processResponse(posList, response, ret),
      error => {
        logError(`${this._name} Failed to get bids: ${error || ''}`);
        return ret;
      }
    );
  }
}

export default _openrtbBase;
